import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, Alert } from 'react-native';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { spacing, fonts, fontWeights, borderRadius } from '../theme';
import { calculateULCashflow, calculateInsuranceAge } from '../core/calculations';
import { RIDERS } from '../data/riders';
import { ColumnInfoModal } from '../components/ColumnInfoModal';

// Flat-shape list for backward-compat with M13 UI rendering.
// `limit` is the coverage cap, sourced from the rider's defaultCoverageLimit
// (formatted manually for display since R3/R4/R5 use different units).
const RIDERS_DATA = RIDERS.map((r) => ({
  id: r.id,
  name: r.name,
  price: r.basePrice,
  desc: r.shortDesc,
  color: r.color,
  category: r.category,
  limit: formatLimit(r.id, r.defaultCoverageLimit),
}));

function formatLimit(id: string, n: number): string {
  if (id === 'R1') return '125.000.000';
  if (id === 'R3') return `${formatNumber(n)} đ/ngày`;
  if (id === 'R5') return `${formatNumber(n)}`;
  return formatNumber(n);
}
function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

const PETAL_DETAILS = [
  {
    id: 'R1',
    name: 'Sống Khỏe Mỗi Ngày',
    color: '#E11D48',
    icon: 'favorite',
    bullets: [
      'Hạn mức bảo vệ y tế lên đến 1 Tỷ VNĐ/năm.',
      'Chi trả 100% chi phí điều trị nội trú, phẫu thuật thực tế tại bệnh viện.',
      'Bảo lãnh viện phí 24/7 tại hệ thống hơn 300 bệnh viện chất lượng cao trong nước.',
    ]
  },
  {
    id: 'R2',
    name: 'Lá Chắn Xanh',
    color: '#D97706',
    icon: 'coronavirus',
    bullets: [
      'Bảo vệ tài chính trước 134 Bệnh lý nghiêm trọng qua nhiều giai đoạn.',
      'Chi trả tối đa lên đến 325% Số tiền bảo hiểm.',
      'Hỗ trợ tài chính khẩn cấp giúp khách hàng an tâm tiếp cận các liệu trình điều trị tiên tiến.'
    ]
  },
  {
    id: 'R3',
    name: 'Dự Phòng Xanh',
    color: '#00A651',
    icon: 'payments',
    bullets: [
      'Miễn đóng toàn bộ phí bảo hiểm còn lại của hợp đồng khi Người mua xảy ra biến cố sức khỏe lớn.',
      'Đảm bảo hợp đồng bảo hiểm chính và các quyền lợi bổ trợ khác tiếp tục duy trì hiệu lực.',
      'Đảm bảo tương lai học vấn của con cái và gia đình không bị ảnh hưởng đứt quãng.'
    ]
  },
  {
    id: 'R4',
    name: 'Hộ Vệ Xanh',
    color: '#2563EB',
    icon: 'airline_seat_flat',
    bullets: [
      'Chi trả lên tới 300% số tiền bảo hiểm đối với thương tật do tai nạn.',
      'Bảo vệ 24/7 phạm vi toàn cầu bao gồm tai nạn giao thông, sinh hoạt, thể thao, lao động.',
      'Hỗ trợ chi phí nằm viện nội trú đặc biệt do tai nạn.'
    ]
  },
  {
    id: 'R5',
    name: 'Bảo Thân Xanh',
    color: '#7C3AED',
    icon: 'local_hospital',
    bullets: [
      'Trợ cấp tiền mặt từ 500.000đ đến 2.000.000đ/ngày nằm viện thực tế để bù đắp thu nhập.',
      'Nhân đôi mức trợ cấp khi nằm viện điều trị tại Khoa chăm sóc đặc biệt (ICU).',
      'Thủ tục thanh toán đơn giản, nhanh chóng qua ứng dụng di động.'
    ]
  }
];

export const M13ShareScreen: React.FC = () => {
  const route = useRoute<RouteProp<RootStackParamList, 'M13ShareScreen'>>();
  const [selectedPetal, setSelectedPetal] = useState<number>(0);
  const [activeTableTab, setActiveTableTab] = useState<'overview' | 'estimation' | 'overview_products'>('overview');
  const [selectedScenario, setSelectedScenario] = useState<'high' | 'low'>('high');
  const [showFullTable, setShowFullTable] = useState<boolean>(false);

  // Decode data from route param or window search query
  let dataStr = route.params?.data;
  if (!dataStr && typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    dataStr = searchParams.get('data') || undefined;
  }

  let customerData = {
    name: 'Hoàng Cao Minh',
    age: 34,
    gender: 'Nam' as 'Nam' | 'Nữ',
    sa: '500.000.000',
    term: '20 năm',
    freq: 'Hàng năm',
    prem: '11.790.000',
    riders: ['R1', 'R5'], // Sống Khỏe Mỗi Ngày + Bảo Thân Xanh (tặng kèm)
    topUpAmount: 0,
    topUpStartYear: 1,
    topUpDuration: 0,
    r5Mode: 'complimentary' as 'complimentary' | 'attached',
    // Custom configurations
    r1Package: 'silver' as 'silver' | 'gold' | 'platinum',
    r2LimitCuoi: 100_000_000,
    r2LimitSom: 0,
    r2LimitDacBiet: 0,
    r3DailyAllowance: 1_000_000,
    r4PhysicalLimit: 100_000_000,
    r5SumAssured: 750_000_000,
    r5Term: '20_years' as '20_years' | 'whole_life',
    riderPremiumOverrides: undefined as Record<string, number> | undefined,
  };

  if (dataStr) {
    try {
      const decodedJson = decodeURIComponent(escape(atob(dataStr)));
      const parsed = JSON.parse(decodedJson);
      customerData = {
        name: parsed.name || 'Khách hàng',
        age: parsed.age || 30,
        gender: parsed.gender || 'Nam',
        sa: parsed.sa || '1.000.000.000',
        term: parsed.term || '20 năm',
        freq: parsed.freq || 'Hàng năm',
        prem: parsed.prem || '15.209.000',
        riders: parsed.riders || [],
        topUpAmount: parsed.topUpAmount || 0,
        topUpStartYear: parsed.topUpStartYear || 1,
        topUpDuration: parsed.topUpDuration || 0,
        r5Mode: parsed.r5Mode || 'complimentary',
        r1Package: parsed.r1Package || 'silver',
        r2LimitCuoi: parsed.r2LimitCuoi ?? 100_000_000,
        r2LimitSom: parsed.r2LimitSom ?? 0,
        r2LimitDacBiet: parsed.r2LimitDacBiet ?? 0,
        r3DailyAllowance: parsed.r3DailyAllowance ?? 1_000_000,
        r4PhysicalLimit: parsed.r4PhysicalLimit ?? 100_000_000,
        r5SumAssured: parsed.r5SumAssured ?? (parsed.r5Mode === 'attached' ? 950_000_000 : 750_000_000),
        r5Term: parsed.r5Term || '20_years',
        riderPremiumOverrides: parsed.riderPremiumOverrides,
      };
    } catch (e) {
      console.warn('Error decoding Base64 sharing parameters:', e);
    }
  }

  const [infoColumnKey, setInfoColumnKey] = useState<string | null>(null);

  const renderTh = (label: string, colKey: string, widthStyle?: any) => (
    <TouchableOpacity
      style={[styles.tableCell, styles.tableHeaderCell, widthStyle, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }, { cursor: 'pointer' } as any]}
      onPress={() => setInfoColumnKey(colKey)}
      activeOpacity={0.6}
    >
      <Text style={[styles.detailTableHeaderCellText, { flexShrink: 1, textAlign: 'right' }]}>{label}</Text>
      <Text style={[styles.headerInfoBadge, { paddingLeft: 3 }]}>ⓘ</Text>
    </TouchableOpacity>
  );

  const parseNumber = (str: string): number => {
    const cleanStr = str.replace(/\./g, '').replace(/,/g, '');
    const parsed = parseInt(cleanStr, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const totalPremiumNum = parseNumber(customerData.prem);

  const ridersTotal = customerData.riders.reduce((sum, id) => {
    const overridePrice = customerData.riderPremiumOverrides?.[id];
    if (overridePrice !== undefined) {
      return sum + overridePrice;
    }
    const rider = RIDERS_DATA.find(r => r.id === id);
    return sum + (rider?.price || 0);
  }, 0);
  const basePremium = Math.max(0, totalPremiumNum - ridersTotal);

  // Actuarial projections
  const customerDob = (customerData as any).dob || '25/11/1996';
  const insuranceAge = calculateInsuranceAge(customerDob);

  const cashflowInput = {
    age: insuranceAge,
    gender: (customerData.gender === 'Nữ' ? 'Nữ' : 'Nam') as 'Nam' | 'Nữ',
    sumAssured: parseNumber(customerData.sa),
    totalPremium: totalPremiumNum,
    term: parseInt(customerData.term.toString().replace(/[^0-9]/g, ''), 10) || 20,
    selectedRiderIds: customerData.riders,
    topUpAmount: customerData.topUpAmount,
    topUpStartYear: customerData.topUpStartYear,
    topUpDuration: customerData.topUpDuration,
    riderPremiumOverrides: customerData.riderPremiumOverrides || (customerData.r5Mode === 'attached' ? { R5: 796_000 } : undefined),
  };

  const cashflowHigh = calculateULCashflow({ ...cashflowInput, declaredInterestRate: 0.04 });
  const cashflowLow = calculateULCashflow({ ...cashflowInput, declaredInterestRate: 0.005 });

  const cashflow = selectedScenario === 'high' ? cashflowHigh : cashflowLow;

  const year20Low = cashflowLow.find(r => r.year === 20)?.surrenderValue || 0;
  const year20High = cashflowHigh.find(r => r.year === 20)?.surrenderValue || 0;

  const isRiderSelected = (riderId: string): boolean => {
    return customerData.riders.includes(riderId);
  };

  const handleContact = () => {
    Alert.alert(
      'Liên hệ Tư vấn viên',
      'Tư vấn viên hỗ trợ: Nguyễn Văn Cao\nSố điện thoại: 0363077359\nZalo: zalo.me/0363077359\n\nBạn có muốn thực hiện cuộc gọi không?',
      [
        { text: 'Đóng', style: 'cancel' },
        { text: 'Gọi điện', onPress: () => Alert.alert('Thông báo', 'Đang thực hiện kết nối cuộc gọi...') }
      ]
    );
  };

  return (
    <View style={styles.container}>
      <ColumnInfoModal
        visible={!!infoColumnKey}
        columnKey={infoColumnKey}
        onClose={() => setInfoColumnKey(null)}
      />
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        
        {/* Brand Header */}
        <View style={styles.brandHeader}>
          <Text style={styles.brandText}>
            <Text style={{ color: '#00A651' }}>e</Text>Quote
          </Text>
          <Text style={styles.brandSub}>Giải pháp tài chính Manulife dành riêng cho bạn</Text>
        </View>

        {/* Curex Style Portal Header */}
        <View style={styles.curexHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.greetingTitle}>Xin chào, {customerData.name}</Text>
            <Text style={styles.greetingSubtitle}>Sản phẩm Xanh Ước Mơ (Tích Luỹ Giáo Dục 123 (Giải pháp - Thế hệ - Bảo vệ))</Text>
          </View>
          <View style={styles.advisorHeaderBadge}>
            <Text style={styles.advisorName}>NGUYỄN VĂN CAO</Text>
            <Text style={styles.advisorRole}>FA - Chuyên Viên Tư Vấn Tài Chính | ĐT: 0363077359</Text>
          </View>
        </View>

        {/* Three Cards Grid */}
        <View style={styles.gridContainer}>
          {/* Card 1: NĐBH Info */}
          <View style={styles.infoCard}>
            <View style={styles.cardHeaderRow}>
              <View style={styles.infoIconCircle}>
                <Icon name="person" size={18} color="#ffffff" />
              </View>
              <Text style={styles.infoCardTitle}>Người mua & NĐBH</Text>
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoTextName}>{customerData.name}</Text>
              <View style={styles.infoRowLine}>
                <Text style={styles.infoLabel}>Ngày sinh:</Text>
                <Text style={styles.infoValue}>24/01/{2026 - customerData.age} ({customerData.age} tuổi)</Text>
              </View>
              <View style={styles.infoRowLine}>
                <Text style={styles.infoLabel}>Giới tính:</Text>
                <Text style={styles.infoValue}>{customerData.gender}</Text>
              </View>
              <View style={styles.infoRowLine}>
                <Text style={styles.infoLabel}>Nghề nghiệp:</Text>
                <Text style={styles.infoValue}>Nhân viên văn phòng</Text>
              </View>
            </View>
          </View>

          {/* Card 2: Plan Info */}
          <View style={styles.infoCard}>
            <View style={styles.cardHeaderRow}>
              <View style={[styles.infoIconCircle, { backgroundColor: '#00A651' }]}>
                <Icon name="shield" size={18} color="#ffffff" />
              </View>
              <Text style={styles.infoCardTitle}>Giải pháp đề nghị</Text>
            </View>
            <View style={styles.infoContent}>
              <Text style={styles.infoTextName}>Xanh Ước Mơ (Cơ Bản)</Text>
              <View style={styles.infoRowLine}>
                <Text style={styles.infoLabel}>Phí cơ bản:</Text>
                <Text style={styles.infoValue}>{formatNumber(basePremium)} VNĐ</Text>
              </View>
              <View style={styles.infoRowLine}>
                <Text style={styles.infoLabel}>Số tiền BH:</Text>
                <Text style={styles.infoValue}>{customerData.sa} VNĐ</Text>
              </View>
              <View style={styles.infoRowLine}>
                <Text style={styles.infoLabel}>Năm đóng phí:</Text>
                <Text style={styles.infoValue}>{customerData.term}</Text>
              </View>
            </View>
          </View>

          {/* Card 3: Fund Allocation */}
          <View style={styles.infoCard}>
            <View style={styles.cardHeaderRow}>
              <View style={[styles.infoIconCircle, { backgroundColor: '#cbd5e1' }]}>
                <Icon name="analytics" size={20} color={PETAL_DETAILS[selectedPetal].color} />
              </View>
              <Text style={styles.infoCardTitle}>Phân bổ quỹ đầu tư</Text>
            </View>
            <View style={styles.allocationContent}>
              {Platform.OS === 'web' ? (
                <svg width="60" height="60" viewBox="0 0 36 36" style={{ alignSelf: 'center' }}>
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#00A651" strokeWidth="3" strokeDasharray="100 0" strokeDashoffset="25" />
                  <g transform="translate(0, 0)">
                    <text x="18" y="21" dominantBaseline="middle" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#00A651">100%</text>
                  </g>
                </svg>
              ) : (
                <View style={styles.allocationFallbackCircle}>
                  <Text style={styles.allocationFallbackText}>100%</Text>
                </View>
              )}
              <Text style={styles.allocationText}>Quỹ Liên Kết Chung</Text>
              <Text style={styles.allocationRate}>Lãi suất cam kết tối thiểu 0.5% - 4%</Text>
            </View>
          </View>
        </View>

        {/* Total premium Breakdown Card */}
        <View style={styles.premiumCard}>
          <Text style={styles.cardTitle}>💰 Tổng phí năm đầu</Text>
          <View style={styles.premiumGrid}>
            <View style={styles.premiumGridItemMain}>
              <Text style={styles.premiumGridLabelMain}>Tổng phí năm đầu</Text>
              <Text style={styles.premiumGridValueMain}>{formatNumber(totalPremiumNum)} VNĐ</Text>
            </View>
            <View style={styles.premiumGridItem}>
              <Text style={styles.premiumGridLabel}>Phí sản phẩm chính</Text>
              <Text style={styles.premiumGridValue}>{formatNumber(basePremium)} VNĐ</Text>
            </View>
            <View style={styles.premiumGridItem}>
              <Text style={styles.premiumGridLabel}>Phí sản phẩm đính kèm</Text>
              <Text style={styles.premiumGridValue}>{formatNumber(ridersTotal)} VNĐ</Text>
            </View>
            <View style={styles.premiumGridItem}>
              <Text style={styles.premiumGridLabel}>Đầu tư thêm</Text>
              <Text style={styles.premiumGridValue}>
                {customerData.topUpAmount > 0 && customerData.topUpDuration > 0
                  ? `${formatNumber(customerData.topUpAmount * customerData.topUpDuration)} VNĐ`
                  : '0 VNĐ'}
              </Text>
            </View>
          </View>
        </View>

        {/* Product Attachments & Flower SVG Card */}
        <View style={styles.flowerAndRiderCard}>
          <Text style={styles.cardTitle}>🌸 Bông hoa giải pháp tài chính</Text>
          <Text style={styles.flowerSubtitle}>Bấm chọn từng cánh hoa để xem chi tiết quyền lợi</Text>

          {Platform.OS === 'web' ? (
            <View style={styles.svgContainer}>
              <svg width="320" height="320" viewBox="0 0 320 320">
                <g transform="translate(160, 160)">
                  {[
                    { id: 'R1', name: 'Sống Khỏe', emoji: '🩺', color: '#E11D48' },
                    { id: 'R2', name: 'Lá Chắn Xanh', emoji: '🛡️', color: '#D97706' },
                    { id: 'R3', name: 'Dự Phòng Xanh', emoji: '☂️', color: '#00A651' },
                    { id: 'R4', name: 'Hộ Vệ Xanh', emoji: '🏥', color: '#2563EB' },
                    { id: 'R5', name: 'Bảo Thân Xanh', emoji: '❤️', color: '#7C3AED' },
                  ].map((p, idx) => {
                    const isSelected = selectedPetal === idx;
                    const rotateDeg = idx * 72;
                    const isAttached = isRiderSelected(p.id);
                    const scale = isSelected ? 1.08 : 1.0;

                    return (
                      <g
                        key={p.id}
                        transform={`rotate(${rotateDeg}) scale(${scale})`}
                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        onClick={() => setSelectedPetal(idx)}
                      >
                        <path
                          d="M 0 0 C -48 -50, -42 -110, 0 -126 C 42 -110, 48 -50, 0 0 Z"
                          fill={isAttached ? p.color : '#cbd5e1'}
                          fillOpacity={isSelected ? 1 : 0.85}
                          stroke="#ffffff"
                          strokeWidth={isSelected ? 3 : 1.5}
                        />
                        <circle cx="0" cy="-68" r="16" fill="#ffffff" opacity="0.95" />
                        <text x="0" y="-62" textAnchor="middle" fontSize="13">
                          {p.emoji}
                        </text>
                        <text
                          x="0"
                          y="-102"
                          textAnchor="middle"
                          fill="#ffffff"
                          fontSize="9.5"
                          fontWeight="bold"
                        >
                          {p.name}
                        </text>
                      </g>
                    );
                  })}

                  {/* Center core circle */}
                  <circle r="36" fill="#003366" stroke="#ffffff" strokeWidth="2.5" />
                  <text x="0" y="-5" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="bold">
                    MANULIFE
                  </text>
                  <text x="0" y="9" textAnchor="middle" fill="#00A651" fontSize="8.5" fontWeight="700">
                    GIẢI PHÁP
                  </text>
                </g>
              </svg>
            </View>
          ) : (
            <View style={styles.fallbackTextWrap}>
              <Text style={{ fontSize: 13, color: '#64748b' }}>[Biểu đồ Bông hoa giải pháp tương tác]</Text>
            </View>
          )}

          {/* Quick Selector Pills */}
          <View style={styles.petalSelectorRow}>
            {PETAL_DETAILS.map((petal, index) => {
              const active = selectedPetal === index;
              const isSelected = isRiderSelected(petal.id);
              return (
                <TouchableOpacity
                  key={petal.id}
                  style={[
                    styles.petalSelectorPill,
                    active && { backgroundColor: petal.color, borderColor: petal.color }
                  ]}
                  onPress={() => setSelectedPetal(index)}
                >
                  <View style={[styles.pillColorDot, { backgroundColor: isSelected ? petal.color : '#cbd5e1' }]} />
                  <Text style={[styles.petalSelectorPillText, active && { color: '#ffffff', fontWeight: 'bold' }]}>
                    {petal.name.split(' ').slice(-2).join(' ')}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Selected Petal Detail Box */}
          {(() => {
            const petal = PETAL_DETAILS[selectedPetal];
            const isSelected = isRiderSelected(petal.id);
            const riderMeta = RIDERS_DATA.find(r => r.id === petal.id);

            return (
              <View style={[styles.petalDetailCard, { borderLeftColor: petal.color }]}>
                <View style={styles.petalDetailHeader}>
                  <Icon name={petal.icon as any} size={20} color={petal.color} />
                  <Text style={[styles.petalDetailTitle, { color: petal.color }]}>{petal.name}</Text>
                  <View style={[styles.statusBadge, { backgroundColor: isSelected ? 'rgba(0, 166, 81, 0.08)' : 'rgba(239, 68, 68, 0.08)' }]}>
                    <Text style={[styles.statusBadgeText, { color: isSelected ? '#00A651' : '#EF4444' }]}>
                      {isSelected ? (riderMeta?.price === 0 ? 'Tặng kèm' : 'Đang tham gia') : 'Chưa tham gia'}
                    </Text>
                  </View>
                </View>
                <View style={styles.petalDetailBullets}>
                  {petal.bullets.map((bullet, idx) => (
                    <View key={idx} style={styles.bulletRow}>
                      <Icon name="check" size={14} color={isSelected ? '#00A651' : '#cbd5e1'} />
                      <Text style={[styles.bulletText, !isSelected && { color: '#64748b' }]}>{bullet}</Text>
                    </View>
                  ))}
                </View>
                {!isSelected ? (
                  <View style={styles.warningBox}>
                    <Icon name="info" size={16} color="#b45309" />
                    <Text style={styles.warningText}>
                      Quyền lợi bổ trợ chưa được tham gia. Liên hệ FA của bạn để được bổ sung với chi phí cực thấp.
                    </Text>
                  </View>
                ) : (
                  <View style={styles.successBox}>
                    <Icon name="verified" size={16} color="#00A651" />
                    <Text style={styles.successText}>
                      Giải pháp đã tối ưu và bao gồm quyền lợi này.
                    </Text>
                  </View>
                )}
              </View>
            );
          })()}
        </View>

        {/* Riders Attachment Cards (Curex "Sản phẩm đính kèm") */}
        <View style={styles.ridersCard}>
          <Text style={styles.cardTitle}>➕ Sản phẩm đính kèm</Text>
          <View style={{ gap: spacing[3] }}>
            {RIDERS_DATA.map((rider) => {
              const isSelected = isRiderSelected(rider.id);
              return (
                <View key={rider.id} style={styles.riderItemRow}>
                  <View style={[styles.riderDot, { backgroundColor: isSelected ? rider.color : '#cbd5e1' }]} />
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.riderNameText, !isSelected && { color: '#64748b' }]}>{rider.name}</Text>
                    <Text style={styles.riderDescText}>{rider.desc}</Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <View style={[styles.statusBadge, { backgroundColor: isSelected ? 'rgba(0, 166, 81, 0.08)' : 'rgba(100, 116, 139, 0.08)' }]}>
                      <Text style={[styles.statusBadgeText, { color: isSelected ? '#00A651' : '#64748b' }]}>
                        {isSelected ? (rider.price === 0 ? 'Tặng kèm' : 'Đang tham gia') : 'Không tham gia'}
                      </Text>
                    </View>
                    <Text style={styles.riderPriceText}>
                      {isSelected ? (rider.price === 0 ? '0 đ' : `${formatNumber(rider.price)} đ`) : '--'}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>

        {/* Green Banner: Tổng kết kế hoạch tài chính */}
        <View style={styles.greenBannerCard}>
          <Text style={styles.greenBannerTitle}>⛳ Tổng kết kế hoạch tài chính</Text>
          <Text style={styles.greenBannerSubtitle}>
            Giá trị dự kiến nhận lại tại năm thứ 20. Kế hoạch giúp bảo vệ tài chính trước mọi rủi ro sức khỏe.
          </Text>
          <View style={styles.bannerGrid}>
            <View style={styles.bannerGridItem}>
              <Text style={styles.bannerGridLabel}>Tổng phí bảo hiểm đóng</Text>
              <Text style={styles.bannerGridValue}>+{formatNumber(basePremium * 20)} đ</Text>
              <Text style={styles.bannerGridSub}>20 năm cơ bản</Text>
            </View>
            <View style={styles.bannerGridItem}>
              <Text style={styles.bannerGridLabel}>Nhận về (0.5% thấp)</Text>
              <Text style={styles.bannerGridValue}>+{formatNumber(year20Low)} đ</Text>
              <Text style={styles.bannerGridSub}>Kịch bản cam kết tối thiểu</Text>
            </View>
            <View style={styles.bannerGridItem}>
              <Text style={styles.bannerGridLabel}>Nhận về (4.0% cao)</Text>
              <Text style={styles.bannerGridValue}>+{formatNumber(year20High)} đ</Text>
              <Text style={styles.bannerGridSub}>Kịch bản giả định đầu tư</Text>
            </View>
          </View>
        </View>

        {/* Bảng ước tính chi tiết */}
        <View style={styles.tableCard}>
          <Text style={styles.cardTitle}>📋 Bảng ước tính chi tiết</Text>
          
          {/* Sub-tabs */}
          <View style={styles.tableTabContainer}>
            <TouchableOpacity
              style={[styles.tableTabBtn, activeTableTab === 'overview' && styles.tableTabBtnActive]}
              onPress={() => setActiveTableTab('overview')}
            >
              <Text style={[styles.tableTabBtnText, activeTableTab === 'overview' && styles.tableTabBtnTextActive]}>
                Tổng quan phí
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tableTabBtn, activeTableTab === 'estimation' && styles.tableTabBtnActive]}
              onPress={() => setActiveTableTab('estimation')}
            >
              <Text style={[styles.tableTabBtnText, activeTableTab === 'estimation' && styles.tableTabBtnTextActive]}>
                Bảng ước tính
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tableTabBtn, activeTableTab === 'overview_products' && styles.tableTabBtnActive]}
              onPress={() => setActiveTableTab('overview_products')}
            >
              <Text style={[styles.tableTabBtnText, activeTableTab === 'overview_products' && styles.tableTabBtnTextActive]}>
                Tổng quan sản phẩm
              </Text>
            </TouchableOpacity>
          </View>

          {/* Sub-tab 1: Tổng quan phí */}
          {activeTableTab === 'overview' && (
            <View style={styles.subTabContent}>
              <Text style={styles.tableSubTitle}>KẾ HOẠCH ĐÓNG PHÍ THEO KỲ</Text>
              
              <View style={styles.responsiveTableWrapper}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    {renderTh('Kế hoạch đóng phí', 'premiumPaid', { width: 140, justifyContent: 'flex-start' })}
                    {renderTh('Năm', 'premiumPaid')}
                    {renderTh('Nửa năm', 'premiumPaid')}
                    {renderTh('Tổng 20 năm', 'premiumPaid', { width: 100 })}
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: 140, fontWeight: 'bold' }]}>Phí cơ bản</Text>
                    <Text style={styles.tableCell}>{formatNumber(basePremium)} đ</Text>
                    <Text style={styles.tableCell}>{formatNumber(Math.round(basePremium * 0.52))} đ</Text>
                    <Text style={[styles.tableCell, { width: 100, color: '#00A651', fontWeight: 'bold' }]}>{formatNumber(basePremium * 20)} đ</Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: 140, fontWeight: 'bold' }]}>Phí bổ trợ</Text>
                    <Text style={styles.tableCell}>{formatNumber(ridersTotal)} đ</Text>
                    <Text style={styles.tableCell}>{formatNumber(Math.round(ridersTotal * 0.52))} đ</Text>
                    <Text style={[styles.tableCell, { width: 100, color: '#00A651', fontWeight: 'bold' }]}>{formatNumber(ridersTotal * 20)} đ</Text>
                  </View>
                  <View style={[styles.tableRow, { backgroundColor: '#f0fdf4' }]}>
                    <Text style={[styles.tableCell, { width: 140, fontWeight: 'bold' }]}>Tổng phí định kỳ</Text>
                    <Text style={styles.tableCell}>{formatNumber(totalPremiumNum)} đ</Text>
                    <Text style={styles.tableCell}>{formatNumber(Math.round(totalPremiumNum * 0.52))} đ</Text>
                    <Text style={[styles.tableCell, { width: 100, color: '#00A651', fontWeight: 'bold' }]}>{formatNumber(totalPremiumNum * 20)} đ</Text>
                  </View>
                </View>
              </View>

              <Text style={[styles.tableSubTitle, { marginTop: spacing[4] }]}>PHÍ BẢO HIỂM THEO MỖI NĂM</Text>
              <View style={styles.responsiveTableWrapper}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    {renderTh('Năm', 'policyYear', { width: 50 })}
                    {renderTh('Tổng phí', 'premiumPaid', { width: 90 })}
                    {renderTh('Phí cơ bản', 'basePremium', { width: 90 })}
                    {renderTh('Sống Khỏe Mỗi Ngày', 'riderPremium')}
                  </View>
                  {[1, 2, 3, 4, 5, 10].map((yr) => (
                    <View key={yr} style={styles.tableRow}>
                      <Text style={[styles.tableCell, { width: 50 }]}>{yr}</Text>
                      <Text style={[styles.tableCell, { width: 90 }]}>{formatNumber(totalPremiumNum)} đ</Text>
                      <Text style={[styles.tableCell, { width: 90 }]}>{formatNumber(basePremium)} đ</Text>
                      <Text style={styles.tableCell}>{isRiderSelected('R1') ? `${formatNumber(1790000)} đ` : '0 đ'}</Text>
                    </View>
                  ))}
                  <View style={[styles.tableRow, { backgroundColor: '#f1f5f9' }]}>
                    <Text style={[styles.tableCell, { width: 50, fontWeight: 'bold' }]}>Tổng</Text>
                    <Text style={[styles.tableCell, { width: 90, fontWeight: 'bold' }]}>{formatNumber(totalPremiumNum * 20)} đ</Text>
                    <Text style={[styles.tableCell, { width: 90, fontWeight: 'bold' }]}>{formatNumber(basePremium * 20)} đ</Text>
                    <Text style={[styles.tableCell, { fontWeight: 'bold' }]}>{isRiderSelected('R1') ? `${formatNumber(1790000 * 20)} đ` : '0 đ'}</Text>
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Sub-tab 2: Bảng ước tính dòng tiền */}
          {activeTableTab === 'estimation' && (
            <View style={styles.subTabContent}>
              <View style={styles.tableScenarioHeader}>
                <Text style={styles.tableScenarioTitle}>Mô phỏng dòng tiền kịch bản</Text>
                <View style={styles.scenarioToggleContainer}>
                  <TouchableOpacity
                    style={[styles.scenarioTogglePill, selectedScenario === 'high' && styles.scenarioTogglePillActive]}
                    onPress={() => setSelectedScenario('high')}
                  >
                    <Text style={[styles.scenarioTogglePillText, selectedScenario === 'high' && styles.scenarioTogglePillActiveText]}>
                      Lãi suất 4.0%
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.scenarioTogglePill, selectedScenario === 'low' && styles.scenarioTogglePillActive]}
                    onPress={() => setSelectedScenario('low')}
                  >
                    <Text style={[styles.scenarioTogglePillText, selectedScenario === 'low' && styles.scenarioTogglePillActiveText]}>
                      Lãi suất 0.5%
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.responsiveTableWrapper}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    {renderTh('Năm/Tuổi', 'policyYear', { width: 55 })}
                    {renderTh('Phí cơ bản', 'basePremium', { width: 85 })}
                    {renderTh('Tiền đi ĐT', 'allocatedBasePremium', { width: 85 })}
                    {renderTh('Phí khấu trừ', 'totalDeduction', { width: 85 })}
                    {renderTh('Giá trị TK', 'accountValue', { width: 85 })}
                    {renderTh('Hoàn lại', 'surrenderValue', { width: 85 })}
                  </View>
                  {cashflow.map((row) => {
                    if (!showFullTable && row.year > 4 && row.year !== 10 && row.year !== 20) return null;
                    return (
                      <View key={row.year} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: 55 }]}>{row.year} / {row.age}</Text>
                        <Text style={[styles.tableCell, { width: 85 }]}>{formatNumber(basePremium)} đ</Text>
                        <Text style={[styles.tableCell, { width: 85 }]}>{formatNumber(basePremium - row.initialCharge)} đ</Text>
                        <Text style={[styles.tableCell, { width: 85 }]}>{formatNumber(row.coi + row.policyFee)} đ</Text>
                        <Text style={[styles.tableCell, { width: 85, fontWeight: 'bold' }]}>{formatNumber(row.accountValue)} đ</Text>
                        <Text style={[styles.tableCell, { width: 85, color: '#00A651', fontWeight: 'bold' }]}>{formatNumber(row.surrenderValue)} đ</Text>
                      </View>
                    );
                  })}
                </View>
              </View>
              <TouchableOpacity style={styles.tableToggle} onPress={() => setShowFullTable(!showFullTable)}>
                <Text style={styles.tableToggleText}>{showFullTable ? 'Thu gọn bảng' : 'Xem bảng đầy đủ →'}</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Sub-tab 3: Tổng quan sản phẩm */}
          {activeTableTab === 'overview_products' && (
            <View style={styles.subTabContent}>
              <View style={styles.responsiveTableWrapper}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.tableCell, styles.tableHeaderCell, { width: 150 }]}>Sản phẩm tham gia</Text>
                    <Text style={[styles.tableCell, styles.tableHeaderCell]}>Số tiền BH</Text>
                    <Text style={[styles.tableCell, styles.tableHeaderCell]}>Thời hạn</Text>
                    <Text style={[styles.tableCell, styles.tableHeaderCell, { width: 100 }]}>Phí đóng</Text>
                  </View>
                  <View style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: 150, fontWeight: 'bold' }]}>Xanh Ước Mơ (Chính)</Text>
                    <Text style={styles.tableCell}>{customerData.sa} đ</Text>
                    <Text style={styles.tableCell}>{customerData.term}</Text>
                    <Text style={[styles.tableCell, { width: 100 }]}>{formatNumber(basePremium)} đ</Text>
                  </View>
                  {customerData.riders.map((id) => {
                    const rider = RIDERS_DATA.find(r => r.id === id);
                    if (!rider) return null;

                    // Determine dynamic sum assured (Số tiền BH)
                    let riderSA = '';
                    if (id === 'R1') {
                      riderSA = customerData.r1Package === 'silver' ? '125.000.000 đ' : customerData.r1Package === 'gold' ? '250.000.000 đ' : '500.000.000 đ';
                    } else if (id === 'R2') {
                      riderSA = `${formatNumber(customerData.r2LimitCuoi)} đ`;
                    } else if (id === 'R3') {
                      riderSA = `${formatNumber(customerData.r3DailyAllowance)} đ/ngày`;
                    } else if (id === 'R4') {
                      riderSA = `${formatNumber(customerData.r4PhysicalLimit)} đ`;
                    } else if (id === 'R5') {
                      riderSA = customerData.r5Mode === 'attached' ? '950.000.000 đ' : '750.000.000 đ';
                    } else {
                      riderSA = '125.000.000 đ';
                    }

                    // Determine dynamic term
                    let riderTerm = 'Tái tục hàng năm';
                    if (id === 'R3' || id === 'R4') {
                      riderTerm = 'Đến 75 tuổi';
                    } else if (id === 'R5') {
                      riderTerm = 'Đến 99 tuổi';
                    }

                    // Determine dynamic premium from overrides or fallback
                    const overridePrice = customerData.riderPremiumOverrides?.[id];
                    const finalPrice = overridePrice !== undefined ? overridePrice : rider.price;

                    return (
                      <View key={id} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: 150, color: rider.color }]}>{rider.name} (Rider)</Text>
                        <Text style={styles.tableCell}>{riderSA}</Text>
                        <Text style={styles.tableCell}>{riderTerm}</Text>
                        <Text style={[styles.tableCell, { width: 100 }]}>
                          {finalPrice === 0 ? 'Tặng kèm' : `${formatNumber(finalPrice)} đ`}
                        </Text>
                      </View>
                    );
                  })}
                  <View style={[styles.tableRow, { backgroundColor: '#f0fdf4' }]}>
                    <Text style={[styles.tableCell, { width: 150, fontWeight: 'bold' }]}>Tổng tất cả</Text>
                    <Text style={styles.tableCell}>--</Text>
                    <Text style={styles.tableCell}>--</Text>
                    <Text style={[styles.tableCell, { width: 100, color: '#00A651', fontWeight: 'bold' }]}>{formatNumber(totalPremiumNum)} đ</Text>
                  </View>
                </View>
              </View>
            </View>
          )}

        </View>

        {/* advisor profile footer */}
        <View style={styles.advisorFooterCard}>
          <View style={{ flex: 1 }}>
            <Text style={styles.footerAdvisorName}>Nguyễn Văn Cao</Text>
            <Text style={styles.footerAdvisorRole}>FA - Chuyên Viên Tư Vấn Tài Chính</Text>
            <View style={styles.footerRowDetail}>
              <Icon name="phone" size={14} color="#64748b" />
              <Text style={styles.footerDetailText}>Số điện thoại: 0363077359</Text>
            </View>
            <View style={styles.footerRowDetail}>
              <Icon name="mail" size={24} color="#0066cc" />
              <Text style={styles.footerDetailText}>Email: ngoalongtb001@gmail.com</Text>
            </View>
          </View>
          <View style={styles.qrContainer}>
            {Platform.OS === 'web' ? (
              <svg width="60" height="60" viewBox="0 0 29 29" style={{ alignSelf: 'center' }}>
                <path d="M0 0h9v9H0zm1 1v7h7V1zm8 8h1v1H9zm0-2h1v1H9zm1 1h1v1h-1zm0-2h1v1h-1zm-9 3h1v1H1zm0 2h1v1H1zm1 1h1v1H2zm0-2h1v1H2zm7-7h1v1H9zm10-3h9v9h-9zm1 1v7h7V1zm-9 10h1v1h-1zm2 1h1v1h-1zm0-3h1v1h-1zm2 2h1v1h-1zm-4-4h1v1h-1zm14 7h1v1h-1zm0-2h1v1h-1zm-9 9h9v9h-9zm1 1v7h7v-7z" fill="#003366" />
              </svg>
            ) : (
              <View style={styles.qrFallback}>
                <Text style={{ fontSize: 9, color: '#64748b' }}>[QR Code]</Text>
              </View>
            )}
          </View>
        </View>

        {/* CTA Contact Button */}
        <TouchableOpacity style={styles.contactBtn} onPress={handleContact} activeOpacity={0.9}>
          <Icon name="chat" size={20} color="#ffffff" />
          <Text style={styles.contactBtnText}>LIÊN HỆ TƯ VẤN VIÊN (NGUYỄN VĂN CAO)</Text>
        </TouchableOpacity>

        {/* Disclaimer Card */}
        <View style={styles.disclaimerCard}>
          <Icon name="gavel" size={16} color="#64748b" />
          <Text style={styles.disclaimerText}>
            <Text style={{ fontWeight: 'bold' }}>Tuyên bố miễn trừ trách nhiệm (Disclaimer): </Text>
            Tài liệu này được tạo ra bởi ứng dụng eQuote (công cụ hỗ trợ tính nhanh không chính thức dành cho FA) chỉ nhằm mục đích tham khảo. Các thông số, số liệu về phí đóng, dòng tiền và quyền lợi chỉ mang tính minh họa giả định tại thời điểm lập và không thay thế cho Bản minh họa sản phẩm chính thức do Tổng công ty Manulife Việt Nam phát hành.
          </Text>
        </View>

      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 40,
    gap: spacing[4],
    maxWidth: 640,
    width: '100%',
    alignSelf: 'center',
  },
  brandHeader: {
    alignItems: 'center',
    paddingVertical: spacing[3],
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  brandText: {
    fontSize: 26,
    fontWeight: fontWeights.bold,
    color: '#003366',
  },
  brandSub: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
  },

  /* Curex Header Style */
  curexHeader: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[3],
    alignItems: 'center',
  },
  greetingTitle: {
    fontSize: 19,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  greetingSubtitle: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
    lineHeight: 16,
  },
  advisorHeaderBadge: {
    backgroundColor: '#f1f5f9',
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[3],
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    alignItems: 'flex-start',
  },
  advisorName: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#003366',
  },
  advisorRole: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 2,
  },

  /* Three Cards Grid */
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[3],
  },
  infoCard: {
    flex: 1,
    minWidth: 180,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: spacing[4],
    gap: spacing[2],
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  infoIconCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#003366',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoCardTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#475569',
  },
  infoContent: {
    gap: spacing[2],
  },
  infoTextName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
    marginBottom: 4,
  },
  infoRowLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 11.5,
  },
  infoLabel: {
    color: '#64748b',
    fontSize: 11.5,
  },
  infoValue: {
    fontWeight: fontWeights.semibold,
    color: '#334155',
    fontSize: 11.5,
  },

  allocationContent: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  allocationFallbackCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 3,
    borderColor: '#00A651',
    alignItems: 'center',
    justifyContent: 'center',
  },
  allocationFallbackText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#00A651',
  },
  allocationText: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
    marginTop: 4,
  },
  allocationRate: {
    fontSize: 9,
    color: '#64748b',
    textAlign: 'center',
  },

  /* Total Premium Card */
  premiumCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: spacing[4],
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#003366',
    marginBottom: spacing[3],
  },
  premiumGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  premiumGridItemMain: {
    flex: 1,
    minWidth: '100%',
    backgroundColor: 'rgba(0, 166, 81, 0.04)',
    borderWidth: 1,
    borderColor: 'rgba(0, 166, 81, 0.15)',
    borderRadius: borderRadius.sm,
    padding: spacing[3],
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  premiumGridLabelMain: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#00A651',
  },
  premiumGridValueMain: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: '#00A651',
  },
  premiumGridItem: {
    flex: 1,
    minWidth: 120,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: borderRadius.sm,
    padding: spacing[2],
    gap: 4,
  },
  premiumGridLabel: {
    fontSize: 10,
    color: '#64748b',
  },
  premiumGridValue: {
    fontSize: 12.5,
    fontWeight: fontWeights.bold,
    color: '#334155',
  },

  /* Flower wrapper card */
  flowerAndRiderCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
  },
  flowerSubtitle: {
    fontSize: 11.5,
    color: '#64748b',
    marginTop: 2,
    marginBottom: spacing[3],
  },
  svgContainer: {
    width: 240,
    height: 240,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fallbackTextWrap: {
    height: 140,
    justifyContent: 'center',
  },
  petalSelectorRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing[2],
    marginTop: spacing[4],
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#cbd5e1',
    paddingTop: spacing[3],
    width: '100%',
  },
  petalSelectorPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
    paddingVertical: spacing[1],
    paddingHorizontal: spacing[2],
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    backgroundColor: '#ffffff',
  },
  pillColorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  petalSelectorPillText: {
    fontSize: 10.5,
    color: '#475569',
  },

  petalDetailCard: {
    width: '100%',
    marginTop: spacing[4],
    padding: spacing[4],
    borderRadius: borderRadius.sm,
    backgroundColor: '#f8fafc',
    borderLeftWidth: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  petalDetailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  petalDetailTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    flex: 1,
  },
  statusBadge: {
    paddingVertical: 2,
    paddingHorizontal: 7,
    borderRadius: borderRadius.full,
  },
  statusBadgeText: {
    fontSize: 9.5,
    fontWeight: fontWeights.bold,
  },
  petalDetailBullets: {
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
  },
  bulletText: {
    flex: 1,
    fontSize: 12.5,
    color: '#334155',
    lineHeight: 17,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    backgroundColor: '#fffbeb',
    borderWidth: 1,
    borderColor: '#fef3c7',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[2],
  },
  warningText: {
    flex: 1,
    fontSize: 11,
    color: '#b45309',
    lineHeight: 15,
  },
  successBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#dcfce7',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[2],
  },
  successText: {
    flex: 1,
    fontSize: 11,
    color: '#15803d',
    lineHeight: 15,
  },

  /* Riders Card */
  ridersCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  riderItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[2],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#cbd5e1',
  },
  riderDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  riderNameText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  riderDescText: {
    fontSize: 10.5,
    color: '#64748b',
    marginTop: 2,
  },
  riderPriceText: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: '#334155',
    marginTop: 2,
  },

  /* Green Banner Card */
  greenBannerCard: {
    backgroundColor: '#00A651',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    gap: spacing[2],
  },
  greenBannerTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  greenBannerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: 15,
  },
  bannerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
    marginTop: spacing[2],
  },
  bannerGridItem: {
    flex: 1,
    minWidth: 140,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.sm,
    padding: spacing[3],
    gap: 3,
  },
  bannerGridLabel: {
    fontSize: 9.5,
    color: '#64748b',
  },
  bannerGridValue: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: '#00A651',
  },
  bannerGridSub: {
    fontSize: 9,
    color: '#94a3b8',
  },

  /* Detailed Tables Card */
  tableCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  tableSubTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#003366',
    letterSpacing: 0.5,
    marginBottom: spacing[2],
  },
  tableTabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#cbd5e1',
    marginBottom: spacing[4],
  },
  tableTabBtn: {
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[4],
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginBottom: -2,
  },
  tableTabBtnActive: {
    borderBottomColor: '#00A651',
  },
  tableTabBtnText: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: fontWeights.medium,
  },
  tableTabBtnTextActive: {
    color: '#00A651',
    fontWeight: fontWeights.bold,
  },

  subTabContent: {
    gap: spacing[2],
  },
  responsiveTableWrapper: {
    width: '100%',
    overflowX: 'auto' as any, // Supported on Web
  },
  table: {
    width: '100%',
    minWidth: 380,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#00A651',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    backgroundColor: '#ffffff',
  },
  tableCell: {
    flex: 1,
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
    fontSize: 11,
    color: '#334155',
    textAlign: 'right',
    fontFamily: fonts.mono,
    fontVariant: ['tabular-nums'],
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    textAlign: 'right',
    fontFamily: fonts.body,
  },
  detailTableHeaderCellText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 11,
    fontFamily: fonts.body,
  },
  headerInfoBadge: {
    fontSize: 11,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
    opacity: 0.9,
  },

  tableScenarioHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[2],
  },
  tableScenarioTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  scenarioToggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderRadius: borderRadius.full,
    padding: 2,
  },
  scenarioTogglePill: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
  },
  scenarioTogglePillActive: {
    backgroundColor: '#003366',
  },
  scenarioTogglePillText: {
    fontSize: 9.5,
    color: '#475569',
    fontWeight: fontWeights.medium,
  },
  scenarioTogglePillActiveText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
  },
  tableToggle: {
    alignItems: 'center',
    paddingVertical: spacing[2],
    marginTop: spacing[1],
  },
  tableToggleText: {
    fontSize: 11.5,
    color: '#003366',
    fontWeight: fontWeights.bold,
  },

  /* Advisor Footer */
  advisorFooterCard: {
    backgroundColor: '#f1f5f9',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    padding: spacing[4],
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
  },
  footerAdvisorName: {
    fontSize: 14.5,
    fontWeight: fontWeights.bold,
    color: '#003366',
  },
  footerAdvisorRole: {
    fontSize: 11,
    color: '#475569',
    marginBottom: spacing[2],
  },
  footerRowDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    marginTop: 3,
  },
  footerDetailText: {
    fontSize: 10.5,
    color: '#475569',
  },
  qrContainer: {
    width: 68,
    height: 68,
    borderRadius: borderRadius.sm,
    backgroundColor: '#ffffff',
    padding: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  contactBtn: {
    backgroundColor: '#003366',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing[3],
    borderRadius: borderRadius.md,
    gap: spacing[2],
    marginTop: spacing[2],
    shadowColor: '#003366',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 3,
  },
  contactBtnText: {
    color: '#ffffff',
    fontSize: 13.5,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.2,
  },

  disclaimerCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
    backgroundColor: 'rgba(100, 116, 139, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(100, 116, 139, 0.1)',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    marginTop: spacing[2],
  },
  disclaimerText: {
    flex: 1,
    fontSize: 11,
    color: '#64748b',
    lineHeight: 16,
    textAlign: 'justify',
  },
});
